import { EndpointBuilder } from "@reduxjs/toolkit/query";
import {
  ApiResponse,
  BaseGetTradeExtraInfoDataRequest,
  CreateTradeRequest,
  EditTradeRequest,
  FixMeLater,
  FreeTextTradeData,
  GetClientsDataFromSymbolRequest,
  GetCreateTradeGeneralDataResponse,
  GetSymbolsData,
  GetSymbolsResponse,
  GetTradeExtraInfoActivitiesResponse,
  GetTradeExtraInfoConfigurationResponse,
  GetTradeExtraInfoGeneralResponse,
  GetTradeExtraInfoOrdersGraphRequest,
  GetTradeExtraInfoOrdersGraphResponse,
  GetTradeExtraInfoOrdersResponse,
  GetTradesDataResponse,
  GetTradesRequest,
  SymbolClientsData,
  ApiTags,
  TradeExtraInfoActivity,
  ApiGeneralDataResponse,
  TradeData,
  RawFiltersData,
  TradesGeneralDataResponse,
  SellPartialTradeRequest,
  TradeFirstEntry,
  MoveEntryToCurrentPriceRequest,
  BaseTradeActionRequest,
  TradeQuantityLimit,
  GridCardData,
  TradeExtraInfoStats,
  TradeMetadata,
  GetCreateTradeGeneralDataRequest,
  GetTradesGraphsDataRequest,
  BotGraphsData,
} from "@shared/types";
import {
  closedTradesFilters,
  detailsDrawerClosedTradesFilters,
} from "@shared/slices/filters.slice";
import { convertToObjectsArray } from "@shared/utils/arrays";
import {
  transformActivities,
  transformConfiguration,
  transformGeneral,
  transformGrids,
  transformMetadata,
  transformOrders,
  transformStats,
} from "@shared/features/trades/common/tradeExtraDetailsTransforms";
import { getTradesTransformResponse } from "@shared/features/trades/common/utils";
import { getJobQuery, getMutationJobQuery } from "@shared/services/jobs/utils";
import { INITIAL_DATA_MAX_TIME_SECONDS } from "@shared/common/constants";
import moment from "moment/moment";

export const tradesEndpoints = {
  endpoints: (build: EndpointBuilder<any, any, any>) => ({
    getOpenTradesData: getJobQuery({
      build,
      endpointName: "getOpenTradesDataJob",
      transformResponse: (response, args) => {
        return getTradesTransformResponse(response, args, true);
      },
      getArgs: (args) => ({
        ...args,
        add_extra_info: true,
        include_closest_pending_order: true,
      }),
      providesTags: [{ type: ApiTags.TRADES, id: "LIST" }],
      keepUnusedDataFor: INITIAL_DATA_MAX_TIME_SECONDS,
    }),
    getClosedTradesFilters: build.query<
      ApiGeneralDataResponse<RawFiltersData>,
      { inDrawer?: boolean } | void
    >({
      query: () => {
        return {
          url: "get_closed_trades_filters/",
          method: "POST",
        };
      },
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const filtersSlice = args?.inDrawer
            ? detailsDrawerClosedTradesFilters
            : closedTradesFilters;
          dispatch(filtersSlice.actions.loadFilters({ data }));
        } catch {}
      },
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<RawFiltersData>>
      ) => {
        return response.data;
      },
    }),
    getClosedTradesData: build.query<
      TradesGeneralDataResponse<TradeData[]>,
      GetTradesRequest | void
    >({
      query: (args) => {
        return {
          url: "get_closed_trades_data/",
          method: "POST",
          body: { ...args, add_extra_info: true },
        };
      },
      transformResponse: (
        response: ApiResponse<GetTradesDataResponse>,
        _,
        args
      ) => {
        return getTradesTransformResponse(response, args);
      },
      providesTags: [{ type: ApiTags.CLOSED_TRADES, id: "LIST" }],
      keepUnusedDataFor: 0,
    }),
    cancelTrade: build.mutation<void, { trade_id: number; ratio: number }>({
      query: (args) => ({
        url: "cancel_trade/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: (response, _, args) =>
        response
          ? [
              { type: ApiTags.TRADES, id: args.trade_id },
              { type: ApiTags.CLOSED_TRADES, id: "LIST" },
              { type: ApiTags.TRADES, id: "LIST" },
            ]
          : [],
    }),
    bulkCancelTrades: getMutationJobQuery({
      build,
      endpointName: "bulkCancelTradesJob",
      invalidatesTags: (response, _, args) =>
        response
          ? [
              ...args.trade_ids.map((id) => ({ type: ApiTags.TRADES, id })),
              { type: ApiTags.TRADES, id: "LIST" },
            ]
          : [],
    }),
    getTradeExtraInfoGrids: build.query<
      GridCardData[],
      BaseGetTradeExtraInfoDataRequest
    >({
      query: (args) => ({
        url: "get_trade_extra_info_grids/",
        method: "POST",
        body: args,
      }),
      transformResponse: transformGrids,
      providesTags: (_, __, args) => [
        { type: ApiTags.TRADES, id: args.trade_id },
      ],
      keepUnusedDataFor: 0,
    }),
    getTradeExtraInfoStats: build.query<
      TradeExtraInfoStats,
      BaseGetTradeExtraInfoDataRequest
    >({
      query: (args) => ({
        url: "get_trade_extra_info_stats/",
        method: "POST",
        body: args,
      }),
      transformResponse: transformStats,
      providesTags: (_, __, args) => [
        { type: ApiTags.TRADES, id: args.trade_id },
      ],
      keepUnusedDataFor: 0,
    }),
    getTradeExtraInfoOrders: build.query<
      GetTradeExtraInfoOrdersResponse,
      BaseGetTradeExtraInfoDataRequest
    >({
      query: (args) => ({
        url: "get_trade_extra_info_orders/",
        method: "POST",
        body: args,
      }),
      transformResponse: transformOrders,
      providesTags: (_, __, args) => [
        { type: ApiTags.TRADES, id: args.trade_id },
      ],
      keepUnusedDataFor: 0,
    }),
    getTradeExtraInfoOrdersGraph: build.query<
      GetTradeExtraInfoOrdersGraphResponse,
      GetTradeExtraInfoOrdersGraphRequest
    >({
      query: (args) => ({
        url: "get_trade_extra_info_orders_graph/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<GetTradeExtraInfoOrdersGraphResponse>
      ) => {
        return {
          precision: response.data.precision,
          data: response.data.data,
        };
      },
      providesTags: (_, __, args) =>
        "trade_id" in args ? [{ type: ApiTags.TRADES, id: args.trade_id }] : [],
      keepUnusedDataFor: 0,
    }),
    getTradeExtraInfoGeneral: build.query<
      GetTradeExtraInfoGeneralResponse,
      BaseGetTradeExtraInfoDataRequest
    >({
      query: (args) => ({
        url: "get_trade_extra_info_general/",
        method: "POST",
        body: args,
      }),
      transformResponse: transformGeneral,
      providesTags: (_, __, args) => [
        { type: ApiTags.TRADES, id: args.trade_id },
      ],
      keepUnusedDataFor: 0,
    }),
    getTradeExtraInfoConfiguration: build.query<
      GetTradeExtraInfoConfigurationResponse,
      BaseGetTradeExtraInfoDataRequest
    >({
      query: (args) => ({
        url: "get_trade_extra_info_configuration_generic/",
        method: "POST",
        body: args,
      }),
      transformResponse: transformConfiguration,
      providesTags: (_, __, args) => [
        { type: ApiTags.TRADES, id: args.trade_id },
      ],
      keepUnusedDataFor: 0,
    }),
    getTradeExtraInfoActivities: build.query<
      TradeExtraInfoActivity[],
      BaseGetTradeExtraInfoDataRequest
    >({
      query: (args) => ({
        url: "get_trade_extra_info_activities/",
        method: "POST",
        body: args,
      }),
      transformResponse: transformActivities,
      providesTags: (_, __, args) => [
        { type: ApiTags.TRADES, id: args.trade_id },
      ],
      keepUnusedDataFor: 0,
    }),
    getTradeMetadata: build.query<
      TradeMetadata,
      BaseGetTradeExtraInfoDataRequest
    >({
      query: (args) => ({
        url: "get_trade_metadata/",
        method: "POST",
        body: args,
      }),
      transformResponse: transformMetadata,
      providesTags: (_, __, args) => [
        { type: ApiTags.TRADES, id: args.trade_id },
      ],
      keepUnusedDataFor: 0,
    }),
    getSymbols: build.query<GetSymbolsData, void>({
      query: () => ({
        url: "get_symbols/",
        method: "POST",
      }),
      transformResponse: (response: ApiResponse<GetSymbolsResponse>) => {
        return response.data.data;
      },
    }),
    getClientsDataFromSymbol: build.query<
      SymbolClientsData[],
      GetClientsDataFromSymbolRequest
    >({
      query: (args) => ({
        url: "get_clients_data_from_symbol/",
        method: "POST",
        body: { ...args, is_web: true },
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<any[][]>>
      ) =>
        convertToObjectsArray(
          response?.data?.data || [],
          response?.data?.headers || []
        ),
    }),
    freeTextCreateTrade: build.mutation<
      FreeTextTradeData,
      { free_text: string }
    >({
      query: (args) => ({
        url: "free_text_create_trade/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: ApiResponse<FreeTextTradeData>) =>
        response.data,
    }),
    getCreateTradeGeneralData: build.query<
      GetCreateTradeGeneralDataResponse,
      GetCreateTradeGeneralDataRequest
    >({
      query: (args) => ({
        url: "get_trade_create_general_data/",
        method: "POST",
        body: { ...args, is_web: true },
      }),
      transformResponse: (
        response: ApiResponse<GetCreateTradeGeneralDataResponse>
      ) => response.data,
      keepUnusedDataFor: 0,
    }),
    createTrade: build.mutation<FixMeLater, CreateTradeRequest>({
      query: (args) => ({
        url: "create_trade/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: (response) =>
        response
          ? [
              { type: ApiTags.TRADES, id: "LIST" },
              { type: ApiTags.MAIN_COINS_AVAILABLE_AMOUNT, id: "LIST" },
            ]
          : [],
    }),
    editTrade: build.mutation<void, EditTradeRequest>({
      query: (args) => ({
        url: "edit_trade/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: (response) =>
        response
          ? [
              { type: ApiTags.TRADES, id: "LIST" },
              { type: ApiTags.MAIN_COINS_AVAILABLE_AMOUNT, id: "LIST" },
            ]
          : [],
    }),
    editTradeConfiguration: build.mutation<
      void,
      { configuration: FixMeLater; trade_id: number }
    >({
      query: (args) => ({
        url: "edit_trade_configuration/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: (response, _, args) =>
        response ? [{ type: ApiTags.TRADES, id: args.trade_id }] : [],
    }),
    getNumberOfTradesGraph: build.query<FixMeLater, { time_range: string }>({
      query: (args) => ({
        url: "get_number_of_trades_graph/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: ApiResponse<FixMeLater>) => response.data,
      keepUnusedDataFor: 0,
    }),
    getFirstEntry: build.query<TradeFirstEntry, BaseTradeActionRequest>({
      query: (args) => ({
        url: "get_first_entry/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<TradeFirstEntry>>
      ) => response.data.data,
    }),
    getTradeQuantityLimits: build.query<
      TradeQuantityLimit,
      BaseTradeActionRequest
    >({
      query: (args) => ({
        url: "get_trade_quantity_limits/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<TradeQuantityLimit>>
      ) => response.data.data,
    }),
    sellPartialTrade: build.mutation<void, SellPartialTradeRequest>({
      query: (args) => ({
        url: "sell_partial_trade/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: (response, _, args) =>
        response ? [{ type: ApiTags.TRADES, id: args.trade_id }] : [],
    }),
    moveEntryToCurrentPrice: build.mutation<
      void,
      MoveEntryToCurrentPriceRequest
    >({
      query: (args) => ({
        url: "move_entry_to_current_price/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: (response, _, args) =>
        response ? [{ type: ApiTags.TRADES, id: args.trade_id }] : [],
    }),
    getShareTradeImage: build.mutation<
      string,
      BaseGetTradeExtraInfoDataRequest
    >({
      query: (args) => ({
        url: "get_share_trade_image/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<string>>
      ) => response.data.data,
    }),
    exportTrades: getJobQuery({
      build,
      endpointName: "exportTradesJob",
      transformResponse: (response) => {
        return response?.data?.path;
      },
    }),
    exportBacktestTrades: getJobQuery({
      build,
      endpointName: "exportBacktestTradesJob",
      transformResponse: (response) => {
        return response?.data?.path;
      },
    }),
    getTradesGraphsData: build.query<BotGraphsData, GetTradesGraphsDataRequest>(
      {
        query: (args) => ({
          url: "get_trades_graphs_data/",
          method: "POST",
          body: { ...args, timezone_difference: moment().utcOffset() },
        }),
        transformResponse: (
          response: ApiResponse<ApiGeneralDataResponse<BotGraphsData>>
        ) => response.data.data,
      }
    ),
  }),
};
